import { createSelector, createSlice } from '@reduxjs/toolkit';

import {
    CareZone,
    Enterprise,
    Facility,
    FacilityConfig,
    Group,
    NotificationBlock,
    NotificationPolicy,
    ServiceConfiguration,
    StaffMember,
    StaffMemberFacilityMetadata,
    StaffMemberGroupMembership,
} from './sageAdminApi';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import Groups from 'src/constants/Groups';

export interface AppConfigState {
    allGroups: Group[];
    config: ServiceConfiguration;
    currentUser: StaffMember;
    enterprise: Enterprise;
    enterpriseId: string;
    facility: Facility;
    facilityId: string;
    facilityMetadata: StaffMemberFacilityMetadata;
    facilityNotificationPolicies: NotificationPolicy[];
    selectedShiftRid: string;
    staffMemberId: string;
}

export interface UserNotificationContext {
    userStaffProfile: StaffMember;
    userIsOnCall: boolean;
    userGroupMemberships: StaffMemberGroupMembership[];
    userCareZones: CareZone[];
}

type DetectConfig = NonNullable<FacilityConfig['detectConfig']>;
type ObservationsByCareType = NonNullable<FacilityConfig['observationsByCareType']>;

// Shared fallbacks so selectors hand back a stable reference when a value is absent
export const EMPTY_OBJECT = Object.freeze({});
export const EMPTY_LIST = Object.freeze([]) as never[];

export const initialState: AppConfigState = {
    allGroups: [],
    config: {},
    currentUser: {},
    enterpriseId: '',
    enterprise: {
        enterpriseConfig: {
            ssoStatus: 'DISABLED',
        },
    },
    facility: {
        facilityConfig: {
            isAlertLocationEnabled: false,
            isObservationsEnabled: false,
            isExtendedFallVideoEnabled: false,
            isDoorAlertCareConfirmationEnabled: false,
            observationsByCareType: {},
            ehrIntegrationsConfig: {},
            routineCheckIntervalInMin: 0, // 0 == disabled / off
            detectConfig: {},
        },
        ehrLockedFields: [],
    },
    facilityId: '',
    facilityMetadata: {},
    facilityNotificationPolicies: [],
    selectedShiftRid: '',
    staffMemberId: '',
};

export const configSlice = createSlice({
    name: 'appConfig',
    initialState,
    reducers: {
        resetAppConfig: (state) => {
            // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-unused-vars
            state = initialState;
        },
        setAllGroups: (state, { payload }) => {
            state.allGroups = sortBy(payload, ['name']);
        },
        setConfig: (state, { payload }) => {
            state.config = payload;
        },
        setCurrentUser: (state, { payload }) => {
            state.currentUser = payload;
        },
        setEnterprise: (state, { payload }) => {
            state.enterprise = payload;
        },
        setEnterpriseId: (state, { payload }) => {
            state.enterpriseId = payload;
        },
        setFacility: (state, { payload }) => {
            state.facility = payload;
        },
        setFacilityId: (state, { payload }) => {
            state.facilityId = payload;
        },
        setFacilityMetadata: (state, { payload }) => {
            state.facilityMetadata = payload;
        },
        setFacilityNotificationPolicies: (state, { payload }) => {
            state.facilityNotificationPolicies = payload;
        },
        setSelectedShiftRid: (state, { payload }) => {
            state.selectedShiftRid = payload;
        },
        setStaffMemberId: (state, { payload }) => {
            state.staffMemberId = payload;
        },
    },
    selectors: {
        selectAllGroups: (sliceState: AppConfigState) => sliceState.allGroups,
        selectAppConfig: (sliceState: AppConfigState) => sliceState,
        selectConfig: (sliceState: AppConfigState) => sliceState.config,
        selectCurrentUser: (sliceState: AppConfigState) => sliceState.currentUser,
        selectEnterpriseId: (sliceState: AppConfigState) => sliceState.enterpriseId,
        selectFacility: (sliceState: AppConfigState) => sliceState.facility,
        selectFacilityCareZones: (sliceState: AppConfigState) =>
            sliceState.facility?.careZones ?? [],
        selectFacilityId: (sliceState: AppConfigState) => sliceState.facilityId,
        selectFacilityNotificationPolicies: (sliceState: AppConfigState) =>
            sliceState.facilityNotificationPolicies,
        selectCheckInsEnabledCareTypes: (sliceState: AppConfigState) =>
            sliceState.facility?.facilityConfig?.checkInsEnabledCareTypes ?? EMPTY_LIST,
        selectObservationDetailsEnabledCareTypes: (sliceState: AppConfigState) =>
            sliceState.facility?.facilityConfig?.observationDetailsEnabledCareTypes ?? EMPTY_LIST,
        selectGen3Features: (sliceState: AppConfigState) =>
            sliceState.facility?.facilityConfig?.gen3Features ?? EMPTY_LIST,
        selectIsAlertLocationEnabled: (sliceState: AppConfigState) =>
            sliceState.facility?.facilityConfig?.isAlertLocationEnabled || false,
        selectIsExtendedFallVideoEnabled: (sliceState: AppConfigState) =>
            sliceState.facility?.facilityConfig?.isExtendedFallVideoEnabled || false,
        selectIsObservationsEnabled: (sliceState: AppConfigState) =>
            sliceState.facility?.facilityConfig?.isObservationsEnabled || false,
        selectIsDoorAlertCareConfirmationEnabled: (sliceState: AppConfigState) =>
            sliceState.facility?.facilityConfig?.isDoorAlertCareConfirmationEnabled || false,
        selectIsSsoEnabled: (sliceState: AppConfigState) => {
            const { ssoStatus } = sliceState.enterprise?.enterpriseConfig ?? {};
            return ssoStatus === 'ENABLED' || ssoStatus === 'ENFORCED';
        },
        selectIsTaskingEnabled: () => false,
        selectObservationsByCareType: (sliceState: AppConfigState): ObservationsByCareType =>
            sliceState.facility?.facilityConfig?.observationsByCareType ?? EMPTY_OBJECT,
        selectRoutineCheckInterval: (sliceState: AppConfigState) =>
            sliceState.facility?.facilityConfig?.routineCheckIntervalInMin || 0,
        selectDetectConfig: (sliceState: AppConfigState): DetectConfig =>
            sliceState.facility?.facilityConfig?.detectConfig ?? EMPTY_OBJECT,
        selectFacilityMetadata: (sliceState: AppConfigState) => sliceState.facilityMetadata,
        selectSelectedShiftRid: (sliceState: AppConfigState) => sliceState.selectedShiftRid,
        selectStaffMemberId: (sliceState: AppConfigState) => sliceState.staffMemberId,
        selectUserGroupMemberships: (sliceState: AppConfigState) =>
            sliceState.facilityMetadata?.groups ?? [],
        selectIsConfigReady: (sliceState: AppConfigState) => {
            const {
                allGroups,
                config,
                currentUser,
                enterprise,
                enterpriseId,
                facility,
                facilityId,
                facilityMetadata,
                staffMemberId,
            } = sliceState;

            return !(
                isEmpty(allGroups) ||
                isEmpty(config) ||
                isEmpty(currentUser) ||
                !enterprise?.id ||
                isEmpty(enterpriseId) ||
                !facility?.id ||
                isEmpty(facilityId) ||
                isEmpty(facilityMetadata) ||
                isEmpty(staffMemberId)
            );
        },
    },
});

// Action creators are generated for each case reducer function
export const {
    resetAppConfig,
    setAllGroups,
    setConfig,
    setCurrentUser,
    setEnterprise,
    setEnterpriseId,
    setFacility,
    setFacilityId,
    setFacilityMetadata,
    setFacilityNotificationPolicies,
    setSelectedShiftRid,
    setStaffMemberId,
} = configSlice.actions;

export const {
    selectAllGroups,
    selectAppConfig,
    selectCheckInsEnabledCareTypes,
    selectConfig,
    selectCurrentUser,
    selectDetectConfig,
    selectEnterpriseId,
    selectFacility,
    selectFacilityCareZones,
    selectFacilityId,
    selectFacilityMetadata,
    selectFacilityNotificationPolicies,
    selectGen3Features,
    selectIsAlertLocationEnabled,
    selectIsConfigReady,
    selectIsDoorAlertCareConfirmationEnabled,
    selectIsExtendedFallVideoEnabled,
    selectIsObservationsEnabled,
    selectIsSsoEnabled,
    selectObservationDetailsEnabledCareTypes,
    selectObservationsByCareType,
    selectRoutineCheckInterval,
    selectSelectedShiftRid,
    selectStaffMemberId,
    selectUserGroupMemberships,
} = configSlice.selectors;

export const selectIsDetectEnabled = createSelector(selectDetectConfig, (detectConfig) => {
    const detectCameraStatus = detectConfig.detectCameras || 'NONE';
    return detectCameraStatus === 'LIVE' || detectCameraStatus === 'QA';
});

export const selectIsProactiveAlertingEnabled = createSelector(
    [selectIsDetectEnabled, selectDetectConfig],
    (isDetectEnabled, detectConfig) =>
        isDetectEnabled && (detectConfig.isProactiveAlertingEnabled || false)
);

export const selectFacilityOnCallState = createSelector(
    selectFacilityId,
    selectCurrentUser,
    (facilityId, currentUser) =>
        currentUser?.onCallStates?.find((onCallState) => onCallState.facilityId === facilityId)
);

export const selectIsOnCall = createSelector(
    selectFacilityOnCallState,
    (facilityOnCallState) => facilityOnCallState?.isOnCall
);

export const selectStaffMemberFacilityConfigs = createSelector(
    selectFacilityId,
    selectCurrentUser,
    (facilityId, staffMemberProfile) =>
        staffMemberProfile?.staffMemberFacilityConfigs?.find(
            (config) => config.facilityId === facilityId
        )
);

export const selectUserCareZones = createSelector(
    selectFacilityCareZones,
    selectStaffMemberFacilityConfigs,
    (facilityCareZones, staffMemberFacilityConfig) => {
        if (
            !facilityCareZones ||
            !staffMemberFacilityConfig ||
            !staffMemberFacilityConfig.careZones
        ) {
            return [];
        }

        const userCareZoneIds = new Set(
            staffMemberFacilityConfig.careZones
                .filter((careZone) => !!careZone && !!careZone.id)
                .map((careZone) => careZone.id as string)
        );

        return facilityCareZones.filter(
            (careZone) => careZone?.id && userCareZoneIds.has(careZone.id)
        );
    }
);

export const selectUserCareZoneIds = createSelector(
    selectUserCareZones,
    (userCareZones) => userCareZones?.map((careZone) => careZone.id!) ?? []
);

export const selectUserGroupIds = createSelector(
    selectUserGroupMemberships,
    (userGroupMemberships) => userGroupMemberships.map((groupMembership) => groupMembership.groupId)
);

export const selectCareGroupId = createSelector(
    selectAllGroups,
    (groups) => groups?.find((group) => group.name === Groups.CareStaff)?.id
);

export const selectNurseMedtechGroupId = createSelector(
    selectAllGroups,
    (groups) => groups?.find((group) => group.name === Groups.NurseMedTech)?.id
);

export const selectSageSupportGroupId = createSelector(
    selectAllGroups,
    (groups) => groups?.find((group) => group.name === Groups.SageSupport)?.id
);

export const selectIsUserInSageSupportGroup = createSelector(
    selectUserGroupIds,
    selectSageSupportGroupId,
    (userGroupIds, sageSupportGroupId) =>
        sageSupportGroupId != null && userGroupIds.includes(sageSupportGroupId)
);

export const selectAlertNotificationPolicy = createSelector(
    selectFacilityNotificationPolicies,
    (notificationPolicies) => notificationPolicies?.find((policy) => policy.type === 'ALERT')
);

export const selectNonRecurringBlocks = createSelector(
    selectAlertNotificationPolicy,
    (alertNotificationPolicy) =>
        sortBy(
            alertNotificationPolicy?.notificationBlocks.filter(
                (block) => (block.recurringIntervalInMs || 0) <= 0 && !block.isBreakGlass
            ) || [],
            ['executionTimeAfterStartInMs', 'priority']
        )
);

export const selectBreakglassBlocks = createSelector(
    selectAlertNotificationPolicy,
    (alertNotificationPolicy) =>
        sortBy(
            alertNotificationPolicy?.notificationBlocks.filter((block) => !!block.isBreakGlass) ||
                [],
            'priority'
        )
);

export const selectRecurringBlocks = createSelector(
    selectAlertNotificationPolicy,
    (alertNotificationPolicy) =>
        sortBy(
            alertNotificationPolicy?.notificationBlocks.filter(
                (block) => (block.recurringIntervalInMs || 0) > 0
            ) || [],
            'executionTimeAfterStartInMs'
        )
);

export const selectNonRecurringBlocksByExecutionTime = createSelector(
    selectAlertNotificationPolicy,
    (alertNotificationPolicy) => {
        if (!alertNotificationPolicy) {
            return {};
        }

        return alertNotificationPolicy.notificationBlocks.reduce((map, block) => {
            // Do not include recurring or breakglass blocks in this map
            if ((block.recurringIntervalInMs || 0) > 0 || block.isBreakGlass) {
                return map;
            }

            const timeInMinutes = block.executionTimeAfterStartInMs / 60_000;
            const currentBlocks = map[timeInMinutes] || [];
            return {
                ...map,
                [timeInMinutes]: [...currentBlocks, block],
            };
        }, {} as Record<number, NotificationBlock[]>);
    }
);

export const selectUserNotificationContext = createSelector(
    selectCurrentUser,
    selectIsOnCall,
    selectUserGroupMemberships,
    selectUserCareZones,
    (currentUser, isOnCall, userGroupMemberships, userCareZones) => {
        return {
            userStaffProfile: currentUser,
            userIsOnCall: !!isOnCall,
            userGroupMemberships,
            userCareZones,
        };
    }
);

export default configSlice.reducer;
